import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sidebar } from "@/components/Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useFavorites } from "@/hooks/useFavorites";
import { VideoCard } from "@/components/VideoCard";

export default function Favorites() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { favorites, isLoading } = useFavorites();
  const isLiveDemoMode = new URLSearchParams(location.search).get("live") === "1";

  useEffect(() => {
    if (!isLiveDemoMode) return;
    const timer = window.setTimeout(() => {
      navigate("/?live=1", { replace: true });
    }, 6500 + Math.floor(Math.random() * 2500));
    return () => window.clearTimeout(timer);
  }, [isLiveDemoMode, navigate]);

  const handleLogout = async () => {
    await signOut();
    toast({ title: "已退出登录" });
    navigate("/");
  };

  const handleNavigate = (page: string) => {
    if (page === "home") navigate("/");
    else if (page === "vocabulary") navigate("/vocabulary");
    else if (page === "favorites") navigate("/favorites");
    else if (page === "history") navigate("/history");
    else if (page === "feedback") navigate("/feedback");
    else if (page === "profile") navigate("/profile");
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">请先登录以查看收藏</p>
            <Button onClick={() => navigate("/auth")}>去登录</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentPage="favorites"
        onNavigate={handleNavigate}
        isLoggedIn={!!user}
        onLogout={handleLogout}
      />

      <main className={cn(
        "transition-all duration-300",
        sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
      )}>
        <div className="container px-6 py-8 max-w-6xl">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">我的收藏</h1>
              <p className="text-sm text-muted-foreground">
                {isLoading ? "加载中..." : `共 ${favorites.length} 个收藏视频`}
              </p>
            </div>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : favorites.length === 0 ? (
            <Card>
              <CardContent className="pt-12 pb-12 text-center">
                <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  还没有收藏任何视频
                </p>
                <Button onClick={() => navigate("/")}>
                  去发现视频
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {favorites.map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  onClick={() => navigate(`/player/${video.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
